import React from 'react';
import {
  CellStyled,
  ImageContainerStyled,
  RepeaterWrapper,
  StyledLink,
  TableBodyRowCellStyled,
  TableBodyRowStyled,
  TableStyled,
} from './WorkshopsTableSlider.style';
import {
  ContentWrapper,
  HideOnDesktop,
  HideOnMobile,
} from '../../../styles/shared';
import {
  TableBody,
  TableBodyRowCell,
  TableHeader,
} from '../../../components/table/Table';
import { getStrapiMedia } from '../../../utils/media';
import Repeater from '../../../components/repeater/Repeater';
import Link from 'next/link';
import SectionHeader from '../../../components/section-header/SectionHeader';
import { Workshop } from '../../../models/auto-generated-strapi-types/types/Workshop';
import CardWorkshopsMobile from '../../../components/cards/card-workshops-mobile/CardWorkshopsMobile';
import { useRouter } from 'next/router';
import { Slug } from '../../../models/domain/interfaces';
import { getWorkshops } from '../../../utils/api';
import { PersonalizationWts } from '../../../models/auto-generated-strapi-types/types/components/PersonalizationWts';
import { getPersonalizationAllAttributes } from '../../../utils/style';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../../components/image-with-fallback/ImageWithFallback';

interface WorkshopExtended extends Workshop {
  attributes: Workshop['attributes'] & {
    originalDate: string;
  };
}

interface WorkshopTableSlider {
  mainTitle: string;
  title: string;
  workshops: WorkshopExtended[];
  personalization?: PersonalizationWts;
  sortingOptions: string;
}

interface WorkshopTableSliderData {
  id: number;
  title: string;
  image: any;
  date?: Date;
}

const WorkshopTableSilder: React.FC<any> = ({
  data,
  translations,
  isPrerenderRequest,
}: {
  data: WorkshopTableSlider;
  translations: any;
  isPrerenderRequest: boolean;
}) => {
  const { mainTitle, title, workshops, personalization } = data ?? {};
  const router = useRouter();

  const workshopsMapper = (
    workshops: WorkshopExtended[]
  ): WorkshopTableSliderData[] => {
    workshops?.sort((a, b) => {
      const dateA = new Date(a?.attributes?.originalDate);
      const dateB = new Date(b?.attributes?.originalDate);

      switch (data?.sortingOptions) {
        default:
        case 'asc':
          return dateB?.getTime() - dateA?.getTime();
        case 'desc':
          return dateA?.getTime() - dateB?.getTime();
      }
    });

    return workshops?.map<WorkshopTableSliderData>((workshop) => ({
      id: workshop?.id,
      title: workshop?.attributes?.title,
      image: workshop?.attributes?.thumbnail,
      date: workshop?.attributes?.date,
      slug: workshop?.attributes?.slug,
      originalDate: workshop?.attributes?.originalDate,
    }));
  };

  const mappedWorkshops = workshopsMapper(workshops);

  if (!workshops?.length) return <></>;
  return (
    <ContentWrapper>
      <SectionHeader
        subtitle={mainTitle ?? ''}
        title={title ?? ''}
        capitalizeTitle={false}
        mainTitlePersonalization={getPersonalizationAllAttributes(
          personalization as any,
          'mainTitle'
        )}
        titlePersonalization={getPersonalizationAllAttributes(
          personalization as any,
          'title'
        )}
      ></SectionHeader>
      <HideOnMobile>
        <TableStyled>
          <TableHeader data={['', '', '', '']}></TableHeader>
          <TableBody>
            {workshops &&
              workshops.map((row, index) => {
                return (
                  <TableBodyRowStyled
                    key={index}
                    onClick={() =>
                      router.push(
                        `/${translations?.urls?.workshops}/${row?.attributes?.slug}`
                      )
                    }
                  >
                    <TableBodyRowCellStyled>
                      <ImageContainerStyled>
                        <ImageWithFallback
                          isPrerenderRequest={isPrerenderRequest}
                          layout="fill"
                          objectFit="contain"
                          src={getStrapiMedia(
                            row?.attributes?.thumbnail?.data?.attributes?.url ??
                              ''
                          )}
                          alt={
                            row?.attributes?.thumbnail?.data?.attributes
                              ?.alternativeText || ''
                          }
                          fallbackSrc={DEFAULT_IMAGE}
                        />
                      </ImageContainerStyled>
                    </TableBodyRowCellStyled>
                    <TableBodyRowCell>
                      <CellStyled>
                        <span>{translations?.pages?.workshops?.title}</span>
                        {row?.attributes?.registration
                          ? row?.attributes?.titleRegistration ?? '-'
                          : row?.attributes?.title ?? '-'}
                      </CellStyled>
                    </TableBodyRowCell>

                    <TableBodyRowCell>
                      <CellStyled>
                        <span>{translations?.pages?.workshops?.date}</span>
                        {row?.attributes?.date ?? '-'}
                      </CellStyled>
                    </TableBodyRowCell>

                    <TableBodyRowCell>
                      <CellStyled align={'center'}>
                        <Link
                          href={`/${translations?.urls?.workshops}/${row?.attributes?.slug}`}
                          passHref
                        >
                          <StyledLink>
                            {translations?.pages?.workshops?.register}
                          </StyledLink>
                        </Link>
                      </CellStyled>
                    </TableBodyRowCell>
                  </TableBodyRowStyled>
                );
              })}
          </TableBody>
        </TableStyled>
      </HideOnMobile>
      <HideOnDesktop>
        <RepeaterWrapper>
          <Repeater
            spacing={1}
            cols={{ xs: 12, sm: 6 }}
            props={{ translations, isPrerenderRequest }}
            data={mappedWorkshops}
            component={CardWorkshopsMobile}
          />
        </RepeaterWrapper>
      </HideOnDesktop>
    </ContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug
) => {
  const workshops = await getWorkshops({
    locale: slugAnalyzed?.locale,
    pageSize: 12,
    sort: ['date:desc'],
    datesFormat: translations?.date?.workshop?.format,
    filters: {
      registration: {
        $eq: true,
      },
    },
  });
  section.workshops = workshops?.data;
};

export default WorkshopTableSilder;
